import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Webhook, Plus, Copy, Trash2, X, Loader2, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const CrmWebhooksPanel = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm/webhooks`, opts);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement webhooks impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/crm/webhooks`, { name: name.trim() }, opts);
      toast.success('Webhook créé');
      setName('');
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Création échouée');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (webhookId) => {
    if (!window.confirm('Révoquer ce webhook ? Les appels entrants seront refusés.')) return;
    try {
      await axios.delete(`${API}/crm/webhooks/${webhookId}`, opts);
      toast.success('Webhook révoqué');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Révocation échouée');
    }
  };

  const copyUrl = async (token) => {
    const url = `${window.location.origin}/api/crm/webhooks/lead/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL webhook copiée');
    } catch {
      toast.error('Copie impossible');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="crm-webhooks-panel"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,8,12,0.82)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden bg-[#0E1116] max-h-[90vh] flex flex-col"
        style={{ border: '1px solid rgba(255,255,255,0.10)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="px-5 py-4 flex items-center justify-between shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(217,160,91,0.16)', border: '1px solid rgba(217,160,91,0.36)' }}
            >
              <Webhook className="w-4 h-4" style={{ color: '#D9A05B' }} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: '#D9A05B' }}>
                Lead capture
              </p>
              <p className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Webhooks entrants
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="crm-webhooks-close"
            aria-label="Fermer"
            className="rounded-lg p-1.5 transition-colors text-white/55 hover:text-[#D9A05B]"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-[12px] text-white/55 leading-relaxed">
            Créez un endpoint sécurisé pour capter des leads externes (formulaire site, Zapier, n8n, intégration partenaire).
            Chaque appel <code className="text-white/80 bg-white/5 px-1 rounded">POST</code> sur l'URL crée automatiquement un contact dans votre CRM (avec dédoublonnage par email).
          </p>

          <form onSubmit={create} className="flex items-center gap-2" data-testid="crm-webhook-create-form">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom du webhook (ex: Site marketing)"
              className="h-9"
              data-testid="crm-webhook-name-input"
            />
            <Button
              type="submit"
              disabled={creating}
              data-testid="crm-webhook-create-btn"
              className="bg-[#D9A05B] hover:bg-[#C78D46] text-black"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Créer
            </Button>
          </form>

          {loading ? (
            <p className="text-center text-white/40 py-8 text-sm">Chargement…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-white/10 rounded-xl" data-testid="crm-webhooks-empty">
              <Webhook className="w-8 h-8 text-white/20 mx-auto mb-2" />
              <p className="text-white/55 text-sm">Aucun webhook actif.</p>
              <p className="text-white/40 text-xs mt-1">Créez-en un pour capter des leads externes.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="crm-webhooks-list">
              {items.map((w) => {
                const url = `${window.location.origin}/api/crm/webhooks/lead/${w.token}`;
                return (
                  <li
                    key={w.webhook_id}
                    className="rounded-xl p-3 border border-white/10 bg-white/[0.03]"
                    data-testid={`crm-webhook-${w.webhook_id}`}
                    style={{ opacity: w.active ? 1 : 0.5 }}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{w.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-white/45 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {w.hits_count || 0} appel{(w.hits_count || 0) > 1 ? 's' : ''}
                          </span>
                          <span>Créé {fmtDate(w.created_at)}</span>
                          {w.last_hit_at && <span>Dernier hit {fmtDate(w.last_hit_at)}</span>}
                          {!w.active && <span className="text-red-400/80">Révoqué</span>}
                        </div>
                      </div>
                      {w.active && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyUrl(w.token)}
                            data-testid={`crm-webhook-copy-${w.webhook_id}`}
                            className="text-[#D9A05B] hover:text-[#E8B26C] h-8 px-2.5"
                          >
                            <Copy className="w-3.5 h-3.5 mr-1" /> Copier URL
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => revoke(w.webhook_id)}
                            data-testid={`crm-webhook-revoke-${w.webhook_id}`}
                            className="text-red-400/80 hover:text-red-300 h-8 px-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {w.active && (
                      <div className="mt-2 rounded-md bg-black/40 border border-white/5 px-2 py-1.5">
                        <p className="text-[11px] font-mono text-white/70 break-all select-all">{url}</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmWebhooksPanel;
